import { useEffect, useState } from "react";
import { ExternalLink, AlertTriangle } from "lucide-react";
import { api } from "../lib/api.js";
import { Button, PageHeader, Spinner } from "../components/ui.jsx";

// Upstream consoles, embedded for admins.
//
// Coolify loads through /api/admin/consoles/coolify/open, which hands this browser a
// one-time pass into the gated console host (server/consolegate.js). Nobody without
// a panel admin session can reach Coolify's login page at all.
//
// Hetzner is embedded on a best-effort basis: its login lives on another site, runs
// 2FA, and relies on cookies that browsers treat as third-party inside our page —
// Safari and Firefox block those, and Hetzner may refuse framing outright. An iframe
// that fails to load cannot be detected from here (cross-origin), so the page says so
// up front and always offers the new-tab link.
const CONSOLES = {
  coolify: {
    label: "Coolify",
    src: "/api/admin/consoles/coolify/open",
    newTab: "/api/admin/consoles/coolify/open",
    note: "Builds, containers and server configuration. Log in to Coolify once inside the frame.",
  },
  hetzner: {
    label: "Hetzner",
    src: "https://console.hetzner.com/",
    newTab: "https://console.hetzner.com/",
    note: "If this stays blank or shows a login loop, your browser is blocking Hetzner inside another site — use Open in new tab.",
    bestEffort: true,
  },
};

export default function Consoles() {
  const [tab, setTab] = useState(() => {
    try { return localStorage.getItem("dd.consoles.tab") || "coolify"; } catch { return "coolify"; }
  });
  const [coolifyReady, setCoolifyReady] = useState(null); // null = checking
  const [loading, setLoading] = useState(true);

  useEffect(() => { try { localStorage.setItem("dd.consoles.tab", tab); } catch { /* per-viewer nicety only */ } }, [tab]);
  useEffect(() => { setLoading(true); }, [tab]);

  // Is the console configured? The portals list only offers the gated route when
  // COOLIFY_CONSOLE_URL is set, so it doubles as the feature check.
  useEffect(() => {
    api.adminPortals()
      .then((d) => setCoolifyReady((d.portals || []).some((p) => p.key === "orchestrator" && p.url.startsWith("/api/admin/consoles/"))))
      .catch(() => setCoolifyReady(false));
  }, []);

  const c = CONSOLES[tab];
  const blocked = tab === "coolify" && coolifyReady === false;

  return (
    <div className="page flex flex-col gap-3" style={{ height: "calc(100vh - 96px)" }}>
      <PageHeader
        title="Consoles"
        subtitle="The platform's upstream consoles, inside the admin panel"
        actions={
          <a href={c.newTab} target="_blank" rel="noreferrer">
            <Button variant="ghost"><ExternalLink className="h-3.5 w-3.5" /> Open in new tab</Button>
          </a>
        }
      />

      <div className="flex flex-wrap items-center gap-2">
        {Object.entries(CONSOLES).map(([key, v]) => (
          <button
            key={key}
            type="button"
            onClick={() => setTab(key)}
            className="rounded-full px-3 py-1 text-xs font-medium"
            style={{
              border: `1px solid ${tab === key ? "var(--accent)" : "var(--border)"}`,
              background: tab === key ? "var(--accent-soft)" : "transparent",
              color: tab === key ? "var(--accent-text)" : "var(--text-muted)",
              cursor: "pointer",
            }}
          >
            {v.label}
          </button>
        ))}
        <span className="text-xs" style={{ color: "var(--text-muted)" }}>{c.note}</span>
      </div>

      {c.bestEffort && (
        <p className="flex items-center gap-1.5 text-xs" style={{ color: "var(--warn)" }}>
          <AlertTriangle className="h-3.5 w-3.5" />
          Best effort — Hetzner's login may not work inside another site.
        </p>
      )}

      <div className="relative flex-1 overflow-hidden rounded-lg" style={{ border: "1px solid var(--border)", background: "var(--surface, #fff)", minHeight: 420 }}>
        {blocked ? (
          <div className="p-6 text-sm" style={{ color: "var(--text-muted)" }}>
            The Coolify console is not configured. Set <code>COOLIFY_CONSOLE_URL</code> on the panel
            and publish the gated route (see <code>deploy/coolify-console/README.md</code>).
          </div>
        ) : tab === "coolify" && coolifyReady === null ? (
          <div className="p-6 text-sm" style={{ color: "var(--text-muted)" }}><Spinner className="mr-2 inline" /> Checking…</div>
        ) : (
          <>
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center text-sm" style={{ color: "var(--text-muted)" }}>
                <Spinner className="mr-2 inline" /> Loading {c.label}…
              </div>
            )}
            <iframe
              key={tab}
              title={`${c.label} console`}
              src={c.src}
              onLoad={() => setLoading(false)}
              style={{ width: "100%", height: "100%", border: 0, position: "relative" }}
              // Same-site console needs its own cookies + forms + scripts. No top
              // navigation: the frame must not be able to redirect the panel itself.
              sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-popups-to-escape-sandbox allow-downloads allow-modals"
              referrerPolicy="no-referrer"
            />
          </>
        )}
      </div>
    </div>
  );
}
